(() => {
  'use strict';

  const PICKER_SELECTOR = 'variant-selects[data-section]';
  const pending = new WeakMap();

  function variantsFor(picker) {
    if (picker.__kiwentVariants) return picker.__kiwentVariants;
    const node = picker.querySelector('script[type="application/json"]');
    try {
      picker.__kiwentVariants = JSON.parse(node?.textContent || '[]');
    } catch (_error) {
      picker.__kiwentVariants = [];
    }
    return picker.__kiwentVariants;
  }

  function selectedValues(picker) {
    return [...picker.querySelectorAll(':scope > .product-form__input')]
      .map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input[type="radio"]:checked')?.value || null;
      })
      .filter((value) => value !== null);
  }

  function selectedVariant(picker) {
    const values = selectedValues(picker);
    return variantsFor(picker).find((variant) =>
      variant.options.length === values.length &&
      variant.options.every((value, index) => value === values[index])
    );
  }

  function updateAccessibility(picker, variant) {
    const groups = [...picker.querySelectorAll(':scope > .product-form__input')];
    const variants = variantsFor(picker);
    groups.forEach((group, optionIndex) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return;
        label.querySelectorAll('.kiwent-variant-status').forEach((node) => node.remove());
        const available = variants.some((candidate) => {
          if (!candidate.available || candidate.options[optionIndex] !== input.value) return false;
          return groups.every((otherGroup, otherIndex) => {
            if (otherIndex === optionIndex) return true;
            const otherValue = otherGroup.querySelector('select')?.value ||
              otherGroup.querySelector('input[type="radio"]:checked')?.value;
            return !otherValue || candidate.options[otherIndex] === otherValue;
          });
        });
        input.classList.toggle('unavailable', !available);
        input.classList.toggle('disabled', !available);
        input.disabled = !available;
        if (!available) {
          const status = document.createElement('span');
          status.className = 'visually-hidden kiwent-variant-status';
          status.textContent = 'Variant sold out or unavailable';
          label.appendChild(status);
        }
      });
    });
    picker.dataset.kiwentVariantId = variant ? String(variant.id) : '';
  }

  function updateForms(picker, variant) {
    if (!variant) return;
    const section = picker.dataset.section;
    const roots = [
      document.getElementById(`product-form-${section}`),
      document.getElementById(`product-form-installment-${section}`),
      document.querySelector(`product-form[data-section="${CSS.escape(section)}"]`),
      document.getElementById(`ProductForm-${section}`),
    ].filter(Boolean);

    const uniqueRoots = [...new Set(roots)];
    uniqueRoots.forEach((root) => {
      root.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = String(variant.id);
      });
      root.querySelectorAll('button[name="add"], .main-product-atc').forEach((button) => {
        button.disabled = !variant.available;
        button.toggleAttribute('disabled', !variant.available);
        button.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        button.dataset.unavailable = variant.available ? 'false' : 'true';
        const text = button.querySelector('.main-atc__label__text, .button__label');
        if (text) text.textContent = variant.available ? 'Add to cart' : 'Sold out';
      });
    });

    document.querySelectorAll('.main-product-atc').forEach((button) => {
      button.disabled = !variant.available;
      button.toggleAttribute('disabled', !variant.available);
      button.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
    });
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function updatePrice(picker, variant) {
    if (!variant) return;
    const section = picker.dataset.section;
    const price = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    const onSale = compareAt > price;
    const roots = [
      document.getElementById(`price-${section}`),
      document.getElementById(`sticky-atc-separate-price-${section}`),
    ].filter(Boolean);

    roots.forEach((root) => {
      const priceBox = root.matches?.('.price') ? root : root.querySelector('.price');
      if (!priceBox) return;
      priceBox.classList.toggle('price--on-sale', onSale);
      priceBox.classList.toggle('price--sold-out', !variant.available);
      priceBox.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach((node) => {
        node.textContent = formatMoney(price);
      });
      priceBox.querySelectorAll('.price__compare-price .price-item--regular').forEach((node) => {
        node.textContent = onSale ? formatMoney(compareAt) : '';
      });
      const saleBadge = priceBox.querySelector('.price__badge-sale .nowrap');
      if (saleBadge && onSale) saleBadge.textContent = `SAVE ${Math.round((1 - price / compareAt) * 100)}%`;
    });

    document.querySelectorAll(`#main-atc-price-${CSS.escape(section)}, #sticky-atc-price-${CSS.escape(section)}`).forEach((node) => {
      node.textContent = formatMoney(price);
      node.classList.toggle('hidden', !variant.available);
    });
  }

  function updateMedia(picker, variant) {
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;
    const section = picker.dataset.section;
    document.querySelectorAll(`[id^="MediaGallery-${CSS.escape(section)}"]`).forEach((gallery) => {
      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(`${section}-${mediaId}`, true, false, variant);
      }
      const target = gallery.querySelector(`.product__media-item[data-media-id="${CSS.escape(`${section}-${mediaId}`)}"]`);
      if (!target) return;
      gallery.querySelectorAll('.product__media-item[data-media-id]').forEach((node) => {
        node.classList.remove('is-active');
        node.setAttribute('aria-hidden', 'true');
      });
      target.classList.add('is-active');
      target.setAttribute('aria-hidden', 'false');
      const thumbnail = gallery.querySelector(`[data-target="${CSS.escape(`${section}-${mediaId}`)}"] button`);
      gallery.querySelectorAll('[data-target] button[aria-current]').forEach((node) => node.removeAttribute('aria-current'));
      thumbnail?.setAttribute('aria-current', 'true');
    });
  }

  function sync(picker, updateUrl = true) {
    if (!picker?.isConnected) return;
    const variant = selectedVariant(picker);
    updateAccessibility(picker, variant);
    updateForms(picker, variant);
    updatePrice(picker, variant);
    updateMedia(picker, variant);
    if (variant && updateUrl && picker.dataset.updateUrl !== 'false') {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }

  function schedule(picker) {
    const existing = pending.get(picker) || [];
    existing.forEach(clearTimeout);
    const timers = [0, 80, 250, 700, 1400, 2500, 4000].map((delay) =>
      setTimeout(() => sync(picker, true), delay)
    );
    pending.set(picker, timers);
  }

  function initialise(picker) {
    if (picker.dataset.kiwentVariantFix === 'true') return;
    picker.dataset.kiwentVariantFix = 'true';
    sync(picker, false);
    schedule(picker);

    picker.addEventListener('click', () => schedule(picker));
  }

  document.addEventListener('change', (event) => {
    const picker = event.target.closest?.(PICKER_SELECTOR);
    if (picker) schedule(picker);
  }, true);

  function boot() {
    document.querySelectorAll(PICKER_SELECTOR).forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
